import { Alert, Badge, Button, Card, Group, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconChartFunnel, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import DateRangePicker from '../../../components/DateRangePicker/DateRangePicker';
import ErrorMessage from '../../../components/ErrorMessage';
import FunnelChart from '../../../components/Charts/FunnelChart';
import FunnelFormModal from '../../../components/Funnels/FunnelFormModal';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import Spinner from '../../../components/Spinner';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../../helpers/dateUtils';
import { formatFunnelPercent } from '../../../helpers/funnelChart';
import {
  funnelMeasurementCaption,
  funnelSegmentLabel,
  funnelStatsToChartSteps,
  MAX_FUNNELS,
} from '../../../helpers/funnels';
import getErrorMessage from '../../../helpers/getErrorMessage';
import { formatCount } from '../../../helpers/segmentPerformance';
import useFetch from '../../../helpers/useFetch';

const INITIAL_PERIOD_DAYS = 30;

// The dates go out as full ISO datetimes at the local day boundaries, the same
// as AudiencePerformanceTab. This endpoint treats the range as real timestamps
// (half-open: > startDate, <= endDate), so local midnight is exactly the
// boundary the user asked for.
//
// This is deliberately the opposite of the sibling RfmAnalysisTab, which must
// send bare YYYY-MM-DD because that endpoint reduces its params to day labels.
// Do not harmonise the three - the RFM one would start answering for the wrong day.
//
// One debounced call fetches every funnel's stats, rather than one debounced
// call per funnel: AwesomeDebouncePromise collapses concurrent calls to the same
// debounced function, so per-funnel calls in the same tick would all resolve
// with whichever funnel went last. A funnel that fails keeps its own error
// instead of blanking the rest of the page.
const funnelStatsDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, funnelIds, startTs, endTs) =>
    Promise.all(
      funnelIds.map((funnelId) =>
        fetch(
          `/api/admin/accounts/${accountId}/domains/${domainId}/funnels/${funnelId}/stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`
        )
          .then((stats) => [funnelId, { stats }])
          .catch((error) => [funnelId, { error }])
      )
    ).then(Object.fromEntries),
  300
);

const FunnelCard = ({ funnel, result, segments, onEdit, onDelete }) => {
  let content;
  if (result === undefined) {
    content = <Spinner />;
  } else if (result.error) {
    content = <ErrorMessage message={getErrorMessage(result.error)} />;
  } else if (!result.stats?.steps) {
    // useFetch resolves `{}` for a 202/204 and for a 200 whose body fails to
    // parse, so `result.stats` can be `{}` here even though `result.error` is
    // falsy - guard so that shape fails this one card, not the whole tab.
    content = <ErrorMessage message="This funnel returned no data." />;
  } else {
    content = (
      <>
        <FunnelChart data={funnelStatsToChartSteps(result.stats)} height={40 * result.stats.steps.length} />
        <Table mt="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Step</Table.Th>
              <Table.Th>Profiles</Table.Th>
              <Table.Th>% of previous step</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.stats.steps.map((step, index) => (
              <Table.Tr key={index}>
                <Table.Td>{step.name}</Table.Td>
                <Table.Td>{formatCount(step.count)}</Table.Td>
                <Table.Td>{formatFunnelPercent(step.percentOfPrevious)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </>
    );
  }

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Title order={4}>{funnel.name}</Title>
          {funnel.segmentId && (
            <Badge variant="light" color="gray">
              Filtered by segment: {funnelSegmentLabel(segments, funnel.segmentId)}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Button size="xs" variant="default" leftSection={<IconEdit size={14} />} onClick={onEdit}>
            Edit
          </Button>
          <Button size="xs" variant="default" color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
            Delete
          </Button>
        </Group>
      </Group>
      {/* Shared with the At a Glance funnel card, which is where this page's
          visitors arrive from — one sentence, one home (src/helpers/funnels.js),
          so the same funnel cannot read two ways two clicks apart.

          The period is the only bound worth naming: the API hands windowFunnel
          the range's own duration, so `conversionWindowHours` restates the range
          rather than adding a constraint (funnel-reporter.js:49-65). The segment
          is named by the badge above. */}
      <Text size="sm" c="dimmed" mb="sm">
        {funnelMeasurementCaption(funnel)}
      </Text>
      {content}
    </Card>
  );
};

const FunnelsTab = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const [startDate, setStartDate] = useState(daysAgoISODate(INITIAL_PERIOD_DAYS - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [funnels, setFunnels] = useState();
  const [segments, setSegments] = useState();
  const [error, setError] = useState();
  const [results, setResults] = useState();
  // `{}` while creating, `{ funnel }` while editing.
  const [editing, setEditing] = useState();
  const [deleting, setDeleting] = useState();
  const [deleteError, setDeleteError] = useState();
  const [deleteInFlight, setDeleteInFlight] = useState(false);

  const loadFunnels = useCallback(() => {
    setError(undefined);
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/funnels`)
      .then(({ funnels }) => setFunnels(funnels))
      .catch(setError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    loadFunnels();
  }, [loadFunnels]);

  // Only `behavior` segments can filter a funnel - the API rejects any other
  // scope, because that is the only one it can turn into a profile query.
  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=behavior`)
      .then(({ segments }) => setSegments(segments))
      .catch(setError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    if (funnels === undefined) {
      return;
    }

    let cancelled = false;
    setResults(undefined);

    funnelStatsDebounced(
      fetch,
      accountId,
      domainId,
      funnels.map((funnel) => funnel.id),
      dateToStartOfDay(startDate, 'local'),
      dateToEndOfDay(endDate, 'local')
    ).then((byFunnelId) => {
      if (!cancelled) setResults(byFunnelId);
    });

    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, funnels, startDate, endDate]);

  const deleteFunnel = (close) => {
    setDeleteError(undefined);
    setDeleteInFlight(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/funnels/${deleting.id}`, { method: 'DELETE' })
      .then(() => loadFunnels())
      .then(close)
      // ConfirmationModal renders `error.message`, and useFetch rejects with
      // heterogeneous shapes (`{ error }`, `{ error: { message } }`, ...) - none
      // of which have a top-level `.message` - so normalise here.
      .catch((err) => setDeleteError(new Error(getErrorMessage(err))))
      .finally(() => setDeleteInFlight(false));
  };

  const datePicker = (
    <DateRangePicker
      preSetRanges
      startDate={startDate}
      endDate={endDate}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      maxDate={todayISODate()}
    />
  );

  if (error) {
    return (
      <Stack gap="md" mt="md">
        {datePicker}
        <ErrorMessage message={getErrorMessage(error)} />
      </Stack>
    );
  }

  if (funnels === undefined || segments === undefined) {
    return (
      <Stack gap="md" mt="md">
        {datePicker}
        <Spinner />
      </Stack>
    );
  }

  const atCap = funnels.length >= MAX_FUNNELS;

  return (
    <Stack gap="md" mt="md">
      <Group justify="space-between">
        {datePicker}
        <Tooltip
          label={`A domain can have at most ${MAX_FUNNELS} funnels. Delete one to make room.`}
          disabled={!atCap}
          withArrow
        >
          {/* Wrapped so the tooltip still has a hoverable target while the button is disabled. */}
          <span>
            <Button leftSection={<IconPlus size={16} />} disabled={atCap} onClick={() => setEditing({})}>
              Create funnel
            </Button>
          </span>
        </Tooltip>
      </Group>

      {funnels.length === 0 ? (
        <Alert icon={<IconChartFunnel size={16} />} color="gray" variant="light" title="No funnels yet">
          A funnel counts the profiles that completed an ordered series of steps — clicked an email, then added to cart,
          then purchased — so you can see where they drop off. Use <strong>Create funnel</strong> to define one, up to{' '}
          {MAX_FUNNELS} per domain.
        </Alert>
      ) : (
        funnels.map((funnel) => (
          <FunnelCard
            key={funnel.id}
            funnel={funnel}
            result={results?.[funnel.id]}
            segments={segments}
            onEdit={() => setEditing({ funnel })}
            onDelete={() => {
              setDeleteError(undefined);
              setDeleting(funnel);
            }}
          />
        ))
      )}

      {editing && (
        <FunnelFormModal
          funnel={editing.funnel}
          segments={segments}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            loadFunnels();
          }}
        />
      )}

      <ConfirmationModal
        show={!!deleting}
        message={`Delete the funnel "${deleting?.name}"? Its history is not kept.`}
        close={() => setDeleting(undefined)}
        confirm={deleteFunnel}
        error={deleteError}
        loading={deleteInFlight}
      />
    </Stack>
  );
};

export default FunnelsTab;
